import React, { useState, useEffect } from "react";
import { PropTypes } from "prop-types";
import { withTranslation } from "react-i18next";
import MarkdownRenderer from "react-markdown-renderer";
import AceEditor from "react-ace";
import axios from "axios";

import SwitchOnOff from "components/common/tools/SwitchOnOff";
import SegmentedControl from "components/utils/SegmentedControl/SegmentedControl";

import "ace-builds/src-noconflict/mode-html";
import "ace-builds/src-noconflict/ext-searchbox";
import "ace-builds/src-noconflict/ext-language_tools";

import "ace-builds/src-min-noconflict/theme-chrome";
import "ace-builds/src-min-noconflict/theme-monokai";
import rootURL from "utils/RootURL";

function WidgetConfig({ component, description, onChange, t }) {
  const [template, setTemplate] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [component.uuid]);

  const tabs = [
    { type: "html_template", label: t("html_template"), value: 0 },
    { type: "url", label: t("url"), value: 1 },
  ];

  const templateMode = component?.params?.src_type === "html_template";

  const loadTemplate = () => {
    if (component?.params?.template_url) {
      axios({
        url: rootURL(component.params.template_url),
        method: "GET",
      }).then((request) => {
        if (request.status === 200) {
          setTemplate(request.data);
        }
      });
    }
  };

  const onEditTemplate = (value) => {
    onChange(value, "template_temp");
    setTemplate(value);
  };

  const getCurrentTabIndex = () => {
    let index = 0;

    tabs.forEach((tab, i) => {
      if (component?.params?.src_type === tab?.type) {
        index = i;
      }
    });

    return index;
  };

  return (
    <div className="editor-widget">
      <div className="editor-form-section">
        <div className="editor-form-translates editor-widget-settings">
          <div className="editor-form-section-title">{t("widgetSettings")}</div>
          <div className="editor-form-translate">
            <label>{t("widgetWidth")}</label>
            <input
              type="text"
              className="editor-form-input-title"
              value={component.params.width}
              maxLength="128"
              onChange={(e) => onChange(e.target.value, "width")}
            />
          </div>
          <div className="editor-form-translate">
            <label>{t("widgetHeight")}</label>
            <input
              type="text"
              className="editor-form-input-title"
              value={component.params.height}
              maxLength="128"
              onChange={(e) => onChange(e.target.value, "height")}
            />
          </div>
          <div className="editor-form-row editor-form-row-inline m-t-10">
            <div className="editor-form-input">
              <SwitchOnOff
                name="enable_scroll"
                iconName=""
                checked={component.params.enable_scroll}
                size="sm"
                onChange={(value) => onChange(value, "enable_scroll")}
              />
            </div>
            <div className="editor-form-label">{t("enableScroll")}</div>
          </div>
        </div>
      </div>
      <div className="editor-form-section m-b-0">
        <div className="editor-form-section-title">{t("widgetContent")}</div>
        <div className="editor-form-row">
          <div className="editor-form-input">
            <SegmentedControl
              tabs={tabs}
              onChange={(value) => onChange(tabs[value]?.type, "src_type")}
              selected={getCurrentTabIndex()}
            />
          </div>
        </div>
      </div>

      <div className="editor-form-section">
        {templateMode && (
          <div className="editor-widget-panel">
            <div className="editor-widget-header">
              <div className="editor-widget-header-info">
                <MarkdownRenderer markdown={t("template_info")} />
                {description && <MarkdownRenderer markdown={description} />}
              </div>

              <SwitchOnOff
                name="dark-mode-switch"
                iconName={darkMode ? "dark_mode" : "light_mode"}
                checked={darkMode}
                size="sm"
                onChange={setDarkMode}
              />
            </div>

            <AceEditor
              mode="html"
              theme={darkMode ? "monokai" : "chrome"}
              value={template}
              onChange={onEditTemplate}
              name="template-ace-editor"
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
              }}
              width="100%"
              height="600px"
              showPrintMargin={false}
            />
          </div>
        )}

        {!templateMode && (
          <div className="editor-form-translates editor-widget-url">
            <div className="editor-form-translate">
              <label>{t("url_input")}</label>
              <input
                type="text"
                className="editor-form-input-title"
                value={component.params.external_url}
                onChange={(e) => onChange(e.target.value, "external_url")}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

WidgetConfig.propTypes = {
  component: PropTypes.object,
  description: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation("editor-widget")(WidgetConfig);
